'use client'

import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { WatchlistWizard } from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard'
import { SpreaderAssetPicker } from '@/features/spreader/components/spreader-asset-picker'
import type { SpreaderMatrixInstrument } from '@/state/widget-instances'
import type { Instrument } from '@/types/generated/common/types'
import type { WatchlistEntry } from '@/types/generated/public_api'

interface SpreaderTickerPickerProps {
  trigger: ReactElement
  existing: SpreaderMatrixInstrument[]
  preferredBaseCurrency?: string
  onSelect: (instrument: SpreaderMatrixInstrument) => void
  align?: 'start' | 'center' | 'end'
}

/**
 * Shared Spreader instrument picker. The compact popover keeps nearby markets one click
 * away; “Browse all markets” expands into the full contract-level WatchlistWizard without
 * imposing a same-ticker restriction.
 *
 * This wrapper owns both open states so closing the popover cannot unmount and immediately
 * close the wizard that replaces it.
 */
export function SpreaderTickerPicker({
  trigger,
  existing,
  preferredBaseCurrency,
  onSelect,
  align = 'start',
}: SpreaderTickerPickerProps) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [wizardOpen, setWizardOpen] = useState(false)
  const existingEntries = useMemo<WatchlistEntry[]>(
    () => existing.map(({ exchange, symbol }) => ({ exchange, symbol })),
    [existing]
  )

  const selectFromWizard = (instrument: Instrument) => {
    onSelect({ exchange: instrument.exchange, symbol: instrument.symbol })
  }

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent align={align} className="w-auto p-0">
          <SpreaderAssetPicker
            existing={existing}
            preferredBaseCurrency={preferredBaseCurrency}
            onSelect={onSelect}
            onClose={() => setPopoverOpen(false)}
            onBrowseAll={() => {
              setPopoverOpen(false)
              setWizardOpen(true)
            }}
          />
        </PopoverContent>
      </Popover>

      <WatchlistWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        mode="single"
        detailSelectionOnly
        existingEntries={existingEntries}
        onSelect={selectFromWizard}
      />
    </>
  )
}
